// Dialogs for the admin Course view: the shared dialog shell, the module / chapter form and the
// content item form. They send the same requests as the Course Structure Builder's modals
// (AddModuleModal, AddChapterModal, AddContentItemModal, Edit*Modal); only the look is new.
import React, { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { CheckCircle2, FileText, Loader2, Plus, Search, Upload, X } from 'lucide-react';
import useFileUpload from '../../hooks/useFileUpload';
import { api } from '../../api';
import { QUIZ_PICKER_PARAMS, quizzesWithQuestions } from '../../utils/quizPicker';
import { FILE_TYPES, ITEM_TYPES, linkUrlOf } from './courseViewShared';

const ADD_TYPES = [
  ['video', '🎬 Video'],
  ['pdf', '📄 PDF'],
  ['audio', '🎧 Audio'],
  ['image', '🖼️ Image'],
  ['text', '📝 Text'],
  ['link', '🎮 Game / Link'],
  ['quiz', '❓ Quiz'],
  ['task', '✅ Task'],
];
const ACCEPT = { video: 'video/*', image: 'image/*', audio: 'audio/*', pdf: '.pdf,application/pdf' };
const MB = 1024 * 1024;

const isValidUrl = (str) => {
  if (!str || !str.trim()) return true;
  try {
    const url = new URL(str);
    return url.protocol === 'http:' || url.protocol === 'https:';
  } catch {
    return false;
  }
};

const matchesType = (type, file) => {
  switch (type) {
    case 'pdf': return file.type === 'application/pdf';
    case 'video': return file.type.startsWith('video/');
    case 'audio': return file.type.startsWith('audio/');
    case 'image': return file.type.startsWith('image/');
    default: return true;
  }
};

// Dialog shell. `dismissible` lets a click on the backdrop close it (previews); forms close only from their buttons.
export function CvDialog({ title, subtitle, label, wide = false, dismissible = false, onClose, children }) {
  useEffect(() => {
    const onKey = (e) => { if (e.key === 'Escape') onClose(); };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [onClose]);
  return (
    <div className="cv-overlay" onMouseDown={(e) => { if (dismissible && e.target === e.currentTarget) onClose(); }}>
      <div className={`cv-dialog${wide ? ' cv-dialog--wide' : ''}`} role="dialog" aria-modal="true" aria-label={label || title}>
        <header className="cv-dialog__head">
          <div>
            <h3>{title}</h3>
            {subtitle && <small>{subtitle}</small>}
          </div>
          <button type="button" className="cv-dialog__close" aria-label="Close" onClick={onClose}><X size={20} /></button>
        </header>
        {children}
      </div>
    </div>
  );
}

// Add / edit a module or a chapter: title (required) and an optional description.
// `onSubmit` resolves true when saved (the parent then closes the dialog).
export function TitleForm({ heading, noun, initial, placeholder, submitLabel, onSubmit, onClose }) {
  const [title, setTitle] = useState(initial?.title || '');
  const [description, setDescription] = useState(initial?.description || '');
  const [error, setError] = useState('');
  const [saving, setSaving] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    if (!title.trim()) { setError(`${noun} title is required`); return; }
    setSaving(true);
    if (!(await onSubmit({ title: title.trim(), description: description.trim() }))) setSaving(false);
  };

  return (
    <CvDialog title={heading} onClose={onClose}>
      <form className="cv-form" onSubmit={submit} noValidate>
        <div className="cv-dialog__body">
          <label className="cv-field">
            <span>{noun} Title <em>*</em></span>
            <input value={title} maxLength={200} autoFocus placeholder={placeholder} onChange={(e) => { setTitle(e.target.value); setError(''); }} aria-invalid={Boolean(error)} />
            {error && <small className="cv-field__error">{error}</small>}
          </label>
          <label className="cv-field">
            <span>Description <small>(Optional)</small></span>
            <textarea value={description} rows={3} placeholder={`Brief description of this ${noun.toLowerCase()}...`} onChange={(e) => setDescription(e.target.value)} />
          </label>
        </div>
        <footer className="cv-dialog__foot">
          <button type="button" className="cm-btn cm-btn--ghost cm-btn--sm" onClick={onClose} disabled={saving}>Cancel</button>
          <button type="submit" className="cm-btn cm-btn--purple cm-btn--sm" disabled={saving}>{saving ? 'Saving…' : submitLabel}</button>
        </footer>
      </form>
    </CvDialog>
  );
}

// Add a content item to a chapter, or edit one. Add: type, quiz link, title, description, file upload or URL.
// Edit keeps the type and changes title, description and the file / link (as EditContentItemModal does).
export function ContentItemForm({ mode, item, courseId, chapterTitle, onSubmit, onClose }) {
  const navigate = useNavigate();
  const editing = mode === 'edit';
  const [type, setType] = useState(item?.type || 'video');
  const [title, setTitle] = useState(item?.title || '');
  const [description, setDescription] = useState(item?.description || '');
  const [url, setUrl] = useState(item ? (item.type === 'link' ? linkUrlOf(item) : item.fileUrl || '') : '');
  const [quizRef, setQuizRef] = useState('');
  const [quizMode, setQuizMode] = useState('existing');
  const [quizzes, setQuizzes] = useState([]);
  const [loadingQuizzes, setLoadingQuizzes] = useState(false);
  const [search, setSearch] = useState('');
  const [error, setError] = useState('');
  const [saving, setSaving] = useState(false);
  const [dragActive, setDragActive] = useState(false);
  const [picked, setPicked] = useState({ name: '', previewUrl: '', mime: '' });
  const [uploadId, setUploadId] = useState(null);
  const fileInput = useRef(null);
  const { uploads, uploadFile, cancelUpload } = useFileUpload();

  const needsFile = FILE_TYPES.includes(type);
  const uploading = Boolean(uploadId);
  const progress = uploadId ? uploads[uploadId]?.progress || 0 : 0;

  useEffect(() => () => { if (picked.previewUrl) URL.revokeObjectURL(picked.previewUrl); }, [picked.previewUrl]);

  // Same list the builder's "Link Existing" tab shows: published quizzes with questions.
  useEffect(() => {
    if (editing || type !== 'quiz') return undefined;
    let alive = true;
    setLoadingQuizzes(true);
    api.get('/api/v2/lms/admin/quizzes', { params: { status: 'published', ...QUIZ_PICKER_PARAMS } })
      .then((res) => { if (alive && res.data?.success) setQuizzes(quizzesWithQuestions(res.data.quizzes)); })
      .catch(() => { if (alive) setError('Failed to load quizzes'); })
      .finally(() => { if (alive) setLoadingQuizzes(false); });
    return () => { alive = false; };
  }, [editing, type]);

  const changeType = (next) => {
    setType(next);
    setUrl('');
    setQuizRef('');
    setPicked({ name: '', previewUrl: '', mime: '' });
    setError('');
  };

  const handleFile = async (file) => {
    if (!file) return;
    setError('');
    if (!matchesType(type, file)) { setError(`Invalid file type "${file.type}". Expected a ${type} file.`); return; }
    const max = type === 'video' ? 500 * MB : 50 * MB;
    if (file.size > max) { setError(`File too large. Max size: ${type === 'video' ? '500MB' : '50MB'}`); return; }
    setPicked({ name: file.name, previewUrl: URL.createObjectURL(file), mime: file.type });
    const id = `${editing ? `update-${item._id}` : 'upload'}-${Date.now()}`;
    setUploadId(id);
    try {
      const result = await uploadFile({ file, fileType: type, id });
      if (result?.success) setUrl(result.cdnUrl);
    } catch {
      setError('Upload failed. Please try again.');
    } finally {
      setUploadId(null);
    }
  };

  const close = () => {
    if (uploadId) cancelUpload(uploadId);
    onClose();
  };

  const submit = async (e) => {
    e.preventDefault();
    if (!title.trim()) { setError(type === 'quiz' && !editing ? 'Select a quiz to link' : 'Title is required'); return; }
    if (uploading) { setError('Please wait for the file upload to finish'); return; }
    if (needsFile && !url.trim()) { setError(`Please upload a ${type} file or enter its URL`); return; }
    if (url && !url.startsWith('data:') && !isValidUrl(url)) { setError('Please enter a valid URL (starting with http:// or https://)'); return; }

    const payload = { title, description };
    if (needsFile) payload.fileUrl = url;
    if (type === 'link') { payload.fileUrl = url; payload.externalUrl = url; }
    if (!editing) {
      payload.type = type;
      if (type === 'quiz') payload.quizRef = quizRef;
    }
    setSaving(true);
    if (!(await onSubmit(payload))) setSaving(false);
  };

  const shownQuizzes = quizzes.filter((q) => (q.title || '').toLowerCase().includes(search.toLowerCase()));
  const typeMeta = ITEM_TYPES[type] || ITEM_TYPES.file;

  return (
    <CvDialog
      title={editing ? 'Edit Content Item' : 'Add Content Item'}
      subtitle={chapterTitle ? `Chapter: ${chapterTitle}` : undefined}
      onClose={close}
    >
      <form className="cv-form" onSubmit={submit} noValidate>
        <div className="cv-dialog__body">
          {editing ? (
            <p className="cv-form__type"><span aria-hidden="true">{typeMeta.icon}</span>Type: <b>{typeMeta.label}</b></p>
          ) : (
            <label className="cv-field">
              <span>Content Type <em>*</em></span>
              <select value={type} onChange={(e) => changeType(e.target.value)}>
                {ADD_TYPES.map(([value, label]) => <option key={value} value={value}>{label}</option>)}
              </select>
            </label>
          )}

          {type === 'quiz' && !editing && (
            <div className="cv-quizpick">
              <div className="cv-seg" role="tablist" aria-label="Quiz source">
                <button type="button" role="tab" aria-selected={quizMode === 'existing'} className={quizMode === 'existing' ? 'is-active' : ''} onClick={() => setQuizMode('existing')}>Link Existing</button>
                <button type="button" role="tab" aria-selected={quizMode === 'create'} className={quizMode === 'create' ? 'is-active' : ''} onClick={() => setQuizMode('create')}>Create New</button>
              </div>
              {quizMode === 'existing' ? (
                <>
                  <label className="cm-search">
                    <Search size={15} />
                    <input value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Search quizzes..." aria-label="Search quizzes" />
                  </label>
                  <div className="cv-quizpick__list" role="listbox" aria-label="Published quizzes">
                    {loadingQuizzes ? <p className="cv-quizpick__empty">Loading…</p>
                      : !shownQuizzes.length ? <p className="cv-quizpick__empty">No quizzes found</p>
                        : shownQuizzes.map((q) => (
                          <button
                            key={q._id}
                            type="button"
                            role="option"
                            aria-selected={quizRef === q._id}
                            className={quizRef === q._id ? 'is-active' : ''}
                            onClick={() => { setQuizRef(q._id); setTitle(q.title); setDescription(q.description || ''); setError(''); }}
                          >
                            <span>{q.title}</span>
                            {quizRef === q._id && <CheckCircle2 size={16} />}
                          </button>
                        ))}
                  </div>
                </>
              ) : (
                <div className="cv-quizpick__create">
                  <p>Create a new quiz in the Quiz Builder. You will leave this page.</p>
                  <button type="button" className="cm-btn cm-btn--purple cm-btn--sm" onClick={() => { close(); navigate(`/admin/courses/${courseId}/quizzes/create`); }}>
                    <Plus size={15} />Go to Quiz Builder
                  </button>
                </div>
              )}
            </div>
          )}

          {!(type === 'quiz' && !editing && quizMode === 'create') && (
            <>
              <label className="cv-field">
                <span>Title <em>*</em></span>
                <input
                  value={title}
                  maxLength={200}
                  placeholder={type === 'quiz' ? 'Pick a quiz above' : 'e.g., How to Create a Document'}
                  readOnly={type === 'quiz' && !editing}
                  onChange={(e) => { setTitle(e.target.value); setError(''); }}
                />
              </label>
              <label className="cv-field">
                <span>Description <small>(Optional)</small></span>
                <textarea value={description} rows={2} onChange={(e) => setDescription(e.target.value)} />
              </label>
            </>
          )}

          {needsFile && (
            <div className="cv-field">
              <span>{editing ? 'Replace File or URL' : <>Upload File or URL <em>*</em></>}</span>
              <button
                type="button"
                className={`cv-drop${dragActive ? ' is-drag' : ''}`}
                onClick={() => !uploading && fileInput.current?.click()}
                onDragEnter={(e) => { e.preventDefault(); setDragActive(true); }}
                onDragOver={(e) => { e.preventDefault(); setDragActive(true); }}
                onDragLeave={(e) => { e.preventDefault(); setDragActive(false); }}
                onDrop={(e) => { e.preventDefault(); setDragActive(false); handleFile(e.dataTransfer.files?.[0]); }}
                disabled={uploading}
              >
                {uploading ? (
                  <>
                    <Loader2 size={26} className="cv-spin" />
                    <b>{progress >= 100 ? 'Finishing upload…' : `Uploading… ${progress || 0}%`}</b>
                    <span className="cv-drop__bar"><i style={{ width: `${progress || 0}%` }} /></span>
                  </>
                ) : (
                  <>
                    <Upload size={26} />
                    <b>{editing ? 'Click to replace the file' : 'Click to upload or drag & drop'}</b>
                    <small>{type === 'video' ? 'MP4, WebM (Max 500MB)' : type === 'pdf' ? 'PDF (Max 50MB)' : 'Max 50MB'}</small>
                  </>
                )}
              </button>
              <input ref={fileInput} type="file" hidden accept={ACCEPT[type] || '*/*'} onChange={(e) => { handleFile(e.target.files?.[0]); e.target.value = ''; }} data-testid="cv-file-input" />
              {picked.name && <p className="cv-picked"><FileText size={15} /><span>Selected file:</span><b>{picked.name}</b></p>}
              {picked.previewUrl && (
                <div className="cv-picked__preview">
                  {picked.mime.startsWith('image/') ? <img src={picked.previewUrl} alt={`${picked.name} preview`} />
                    : picked.mime.startsWith('audio/') ? <audio controls src={picked.previewUrl} />
                      : picked.mime.startsWith('video/') ? <video controls src={picked.previewUrl} />
                        : picked.mime === 'application/pdf' ? <iframe src={picked.previewUrl} title={`${picked.name} preview`} />
                          : null}
                </div>
              )}
              <input className="cv-input" value={url} onChange={(e) => { setUrl(e.target.value); setError(''); }} placeholder={`Or enter ${type} URL...`} aria-label={`${typeMeta.label} URL`} />
            </div>
          )}

          {type === 'link' && (
            <label className="cv-field">
              <span>Link URL</span>
              <input value={url} onChange={(e) => { setUrl(e.target.value); setError(''); }} placeholder="https://..." />
            </label>
          )}

          {error && <p className="cv-form__error" role="alert">{error}</p>}
        </div>
        <footer className="cv-dialog__foot">
          <button type="button" className="cm-btn cm-btn--ghost cm-btn--sm" onClick={close}>Cancel</button>
          <button type="submit" className="cm-btn cm-btn--purple cm-btn--sm" disabled={saving || uploading || (type === 'quiz' && !editing && quizMode === 'create')}>
            {saving ? 'Saving…' : editing ? 'Update Content' : 'Add Content'}
          </button>
        </footer>
      </form>
    </CvDialog>
  );
}
